import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { serviceChannelApi, departmentApi } from "../api/client";

type ServiceChannelItem = { id: string; name: string };
type DepartmentItem = { id: string; name: string };

export function ServiceStandardFilter() {
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");
  const serviceChannelFilter = findCondition(filters, "serviceChannelId");
  const departmentFilter = findCondition(filters, "departmentId");
  const statusFilter = findCondition(filters, "status");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "name",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "name");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const serviceChannelValue = (serviceChannelFilter?.value as string) || "";
  const departmentValue = (departmentFilter?.value as string) || "";
  const statusValue = (statusFilter?.value as string) || "";

  const hasFilter =
    !!nameFilter?.value ||
    !!serviceChannelValue ||
    !!departmentValue ||
    !!statusValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2" data-tourid="service-standard-filter">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາມາດຕະຖານບໍລິການ (ຊື່ / ຊ່ອງທາງ / ພະແນກ)..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <InfiniteCombobox<ServiceChannelItem>
            queryKey={["service-channels", "filter"]}
            queryFn={(args) =>
              serviceChannelApi.lookup({
                query: args.query,
                pageSize: args.pageSize,
                cursor: args.cursor,
              })
            }
            preloadQueryFn={(id) => serviceChannelApi.hydrate(id)}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={serviceChannelValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "serviceChannelId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "serviceChannelId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ເລືອກຊ່ອງທາງບໍລິການ"
            className="h-8 w-full sm:w-40"
            clearable
          />
          <InfiniteCombobox<DepartmentItem>
            queryKey={["service-standard-departments", "filter"]}
            queryFn={(args) =>
              departmentApi.lookup({
                query: args.query,
                pageSize: args.pageSize,
                cursor: args.cursor,
              })
            }
            preloadQueryFn={(id) => departmentApi.hydrate(id)}
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={departmentValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val) {
                nextFilters = upsertCondition(filters, {
                  field: "departmentId",
                  op: "eq",
                  value: val,
                });
              } else {
                nextFilters = removeConditions(filters, "departmentId");
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ເລືອກພະແນກ"
            className="h-8 w-full sm:w-40"
            clearable
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "active", label: "ໃຊ້ງານ" },
              { value: "draft", label: "ປັບປຸງ" },
              { value: "inactive", label: "ເລີກໃຊ້" },
            ]}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}

